import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { ID, Org, Project } from '@proofroom/schema';
import { Header } from '../components/Header';
import { Tour } from '../components/Tour';
import { HOME_TOUR } from '../components/tourSteps';
import { useTour } from '../hooks/useTour';
import { AgencyRail, useIsAgency } from '../components/AgencyRail';
import { EmptyState, ErrorState, Spinner } from '../components/ui';
import { useProjects } from '../api/queries';
import { useSession } from '../auth/session';
import { orgs, projects as projectsApi } from '../api/endpoints';
import { Activity, SortSelect, sortRecords, type ListSort } from '../components/Activity';
import { BeatJobPicker } from '../components/BeatJobPicker';
import { ProjectDetails } from '../components/ProjectDetails';
import { ApiError } from '../api/client';

export function Projects() {
  const q = useProjects();
  const navigate = useNavigate();
  const [beatOpen, setBeatOpen] = useState(false);
  const agency = useIsAgency();
  const { user } = useSession();
  const isAdmin = !!user && user.role === 'admin';
  const tour = useTour('home');
  const [params, setParams] = useSearchParams();
  const clientFilter = params.get('client') ?? '';
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ListSort>('updated');
  const [limit, setLimit] = useState(25);
  const [clients, setClients] = useState<Org[]>([]);
  const [newClient, setNewClient] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [details, setDetails] = useState<string | null>(null);
  const [clientError, setClientError] = useState('');
  const [clientBusy, setClientBusy] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  useEffect(() => {
    if (isAdmin) orgs.list().then((rows) => setClients(rows.filter((c) => c.kind === 'client'))).catch(() => setClientError('Could not load clients'));
  }, [isAdmin]);
  const visibleClients = new Map((q.data ?? []).filter((p) => p.clientOrgId).map((p) => [p.clientOrgId!, p.clientName ?? 'Client']));
  for (const c of clients) visibleClients.set(c.id, c.name);
  const filtered = sortRecords((q.data ?? []).filter((p) => (!clientFilter || (p.clientOrgId ?? 'unassigned') === clientFilter) &&
    [p.name, p.clientName, p.beatJobNumber].join(' ').toLowerCase().includes(search.toLowerCase())), sort);
  const shown = filtered.slice(0, limit);

  const [newName, setNewName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<ID, string>>({});

  const setRowErr = (id: ID, msg: string) => setRowErrors((prev) => ({ ...prev, [id]: msg }));
  const clearRowErr = (id: ID) => setRowErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreateError(null);
    setCreateBusy(true);
    try {
      if (newClient) await projectsApi.create(newName.trim(), newClient);
      else await projectsApi.create(newName.trim());
      setNewName('');
      await q.refetch();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Could not create the project');
    } finally {
      setCreateBusy(false);
    }
  };

  const handleRename = async (p: Project) => {
    const name = window.prompt('Rename project', p.name);
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === p.name) return;
    try {
      await projectsApi.update(p.id, trimmed);
      clearRowErr(p.id);
      await q.refetch();
    } catch (err) {
      setRowErr(p.id, err instanceof ApiError ? err.message : 'Could not rename this project');
    }
  };

  const handleDelete = async (p: Project) => {
    if (!window.confirm(`Delete ${p.name} and all its documents, drafts, comments and shared links? This cannot be undone.`)) return;
    setDeleting(p.id);
    try {
      await projectsApi.remove(p.id);
      clearRowErr(p.id);
      await q.refetch();
    } catch (err) {
      setRowErr(p.id, err instanceof ApiError ? err.message : 'Could not delete this project');
    } finally { setDeleting(null); }
  };

  return (
    <div className="app">
      <Header crumbs={clientFilter ? [{ label: 'Clients', to: '/projects' }, { label: visibleClients.get(clientFilter) ?? 'Unassigned client' }, { label: 'Projects' }] : [{ label: 'Clients & projects' }]} onTour={tour.start} />
      <main className="main">
        <div className={agency ? 'page with-rail' : 'page'}>
          {agency && <AgencyRail />}
          <div className="page-body" data-tour="projects">
            <div className="page-head"><h1>{clientFilter ? `${visibleClients.get(clientFilter) ?? 'Unassigned client'} projects` : 'Clients & projects'}</h1><span className="sub">{q.data ? `${q.data.length} project${q.data.length === 1 ? '' : 's'}` : ''}</span></div>

            <div className="list-controls" data-tour="clients">
              <label className="list-field">Client<select className="input" aria-label="Filter by client" value={clientFilter} onChange={(e) => { setParams(e.target.value ? { client: e.target.value } : {}); setLimit(25); }}>
                <option value="">All clients</option><option value="unassigned">Unassigned</option>{[...visibleClients].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select></label>
              <label className="list-field">Search<input className="input" type="search" placeholder="Project, client or Beat job" value={search} onChange={(e) => { setSearch(e.target.value); setLimit(25); }} /></label>
              <SortSelect value={sort} onChange={(value) => { setSort(value); setLimit(25); }} />
            </div>
            {isAdmin && <div className="btn-row" style={{ marginBottom: 12 }}><button className="btn" onClick={() => setBeatOpen(true)}>Create from Beat</button></div>}
            {isAdmin && beatOpen && <BeatJobPicker onClose={() => setBeatOpen(false)} onSaved={(project) => { void q.refetch(); setBeatOpen(false); navigate(`/projects/${project.id}`); }} />}
            {isAdmin && <form className="btn-row" style={{ marginBottom: 12 }} onSubmit={async (e) => {
              e.preventDefault(); setClientError(''); setClientBusy(true);
              try { const c = await orgs.create(newClientName.trim()); setClients((prev) => [...prev, c]); setNewClient(c.id); setNewClientName(''); }
              catch (err) { setClientError(err instanceof Error ? err.message : 'Could not add client'); }
              finally { setClientBusy(false); }
            }}><input className="input" aria-label="New client name" placeholder="New client name" maxLength={255} required value={newClientName} onChange={(e) => setNewClientName(e.target.value)} /><button className="btn" disabled={clientBusy || !newClientName.trim()}>Add client</button></form>}
            {clientError && <p className="error-text" role="alert">{clientError}</p>}
            {isAdmin && (
              <form className="card btn-row" style={{ padding: 12, marginBottom: 16 }} onSubmit={(e) => void submitCreate(e)}>
                <input
                  className="input" aria-label="New project name" placeholder="New project name"
                  value={newName} onChange={(e) => setNewName(e.target.value)}
                />
                <select className="input" aria-label="Client for new project" value={newClient} onChange={(e) => setNewClient(e.target.value)}><option value="">Unassigned client</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
                <button className="btn primary" type="submit" disabled={createBusy || !newName.trim()}>{createBusy ? 'Creating' : 'Create'}</button>
                {createError && <p className="error-text" role="alert">{createError}</p>}
              </form>
            )}

            {q.isPending && <Spinner label="Loading projects" />}
            {q.isError && <ErrorState title="Could not load projects" error={q.error} retry={() => void q.refetch()} />}
            {q.data && q.data.length === 0 && <EmptyState title="No projects yet">Ask an administrator to add you to a project.</EmptyState>}
            {q.data && q.data.length > 0 && (
              <div className="card list">
                {shown.map((p) => (
                  <div key={p.id} className="row">
                    <div className="grow">
                      <Link to={`/projects/${p.id}`} className="title">{p.name}</Link>
                      <div className="meta"><Link to={`/projects?client=${p.clientOrgId ?? 'unassigned'}`}>{p.clientName ?? 'Unassigned client'}</Link> / {p.documentCount ?? 0} documents{p.beatJobNumber ? ` · Beat ${p.beatJobNumber}` : ''}</div>
                      <Activity record={p} />
                      {details === p.id && <ProjectDetails project={p} clients={[...visibleClients].map(([id, name]) => ({ id, name, kind: 'client' as const }))} onSaved={() => void q.refetch()} onClose={() => setDetails(null)} />}
                      {rowErrors[p.id] && <div className="error-text small">{rowErrors[p.id]}</div>}
                    </div>
                    {isAdmin ? (
                      <div className="btn-row">
                        <button className="btn sm ghost" onClick={() => setDetails(details === p.id ? null : p.id)}>Details</button>
                        <button className="btn sm ghost" onClick={() => void handleRename(p)}>Rename</button>
                        <button className="btn sm danger" disabled={deleting === p.id} onClick={() => void handleDelete(p)}>Delete</button>
                      </div>
                    ) : (
                      <span className="muted">Open</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {q.data && filtered.length === 0 && q.data.length > 0 && <EmptyState title="No matching projects">Change your search or client filter.</EmptyState>}
            {filtered.length > limit && <button className="btn" onClick={() => setLimit(limit + 25)}>Show more projects ({filtered.length - limit} remaining)</button>}
          </div>
        </div>
      </main>
      {/* held until the list has answered, so the first step has its target to light */}
      <Tour id="home" steps={HOME_TOUR} open={tour.open && !q.isPending} onClose={tour.close} />
    </div>
  );
}
